import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

import { getProductLink } from '../../product/utils';
import { Color as ColorType } from '../../product/types';
import { Color } from './Color';
import { colors, durations } from '../../../design-system';

type Props = {
  productRef: string;
  productName: string;
  colorVariants: ColorType[];
  objectID: string;
  selectedColor: ColorType;
  reverseToolTip: boolean;
  leftAlignToolTip: boolean;
  handleColorClick: (color: ColorType) => void;
  onLinkClick: () => void;
  numberOfColorsShown: number;
};

const StyledContainer = styled.div`
  display: flex;
  padding-left: 2px;
  align-items: center;
  justify-content: left;
  width: 100%;
  gap: 24px;
  height: 28px;

  .color-panel-more-colors-link-wrapper {
    position: relative;

    a {
      text-decoration: none;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.4rem;
      width: 20px;
      height: 20px;
      position: relative;
      z-index: 1;
      transition: background ${durations.FOCUS_DELAY}ms ease-in-out;

      @media (hover: hover) {
        &:hover::before {
          background: ${colors.BACKGROUND};
          border-radius: 4px;
          z-index: -1;
        }
      }

      &::before {
        content: '';
        position: absolute;
        top: -12px;
        bottom: -12px;
        left: -12px;
        right: -12px;
        background: transparent;
        z-index: -1;
      }
    }
  }
`;

export const ColorPanel = ({
  productRef,
  productName,
  colorVariants = [],
  selectedColor,
  objectID,
  reverseToolTip,
  leftAlignToolTip,
  handleColorClick,
  onLinkClick,
  numberOfColorsShown,
}: Props) => {
  const selectedColorItem = colorVariants.find(
    (color) => color.colorRef === selectedColor.colorRef
  );
  const colorsWithSelectedFirst = selectedColorItem
    ? [
        selectedColorItem,
        ...colorVariants.filter((color) => color.colorRef !== selectedColor.colorRef),
      ]
    : colorVariants;

  const numberOfColors = colorVariants.length;

  const [sortedColors] = useState(colorsWithSelectedFirst);

  const productLink = getProductLink({
    productRef,
    productName,
    colorLabel: selectedColor.colorLabel,
    colorRef: selectedColor.colorRef,
  });

  return (
    <StyledContainer id={`catalog-item-color-panel-${objectID}`}>
      {sortedColors?.slice(0, numberOfColorsShown).map((color, index) => {
        const isSelected = color.colorRef === selectedColor.colorRef;
        return (
          <Color
            key={`${productRef}-${color.colorRef}-${index}`}
            productRef={productRef}
            isSelected={isSelected}
            color={color}
            handleColorClick={handleColorClick}
            reverseToolTip={reverseToolTip}
            leftAlignToolTip={leftAlignToolTip}
          />
        );
      })}
      {numberOfColors > numberOfColorsShown && (
        <div className="color-panel-more-colors-link-wrapper">
          <Link
            data-testid={`color-panel-more-colors-link-to-pdp-${objectID}`}
            id={`color-panel-more-colors-link-to-pdp-${objectID}`}
            to={productLink}
            onClick={onLinkClick}
            title="Voir + de couleurs"
          >
            {`+${numberOfColors - numberOfColorsShown}`}
          </Link>
        </div>
      )}
    </StyledContainer>
  );
};
